/**
 * What reaches a person outside the app: three switches per channel, kept on
 * the server (get_notification_preferences / set_notification_preferences,
 * migrations 1009 and 1059) and read by push-notify before it sends. Push is
 * the phone; email goes to the person's address; Slack goes to their own
 * notification channel (Settings, My Connectors), never a shared one. The inbox
 * has every notification either way. Agent roll-ups never leave the app, so
 * they have no switch.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';

export type AlertChannel = 'push' | 'email' | 'slack';
export type PushSwitch = 'asks' | 'live' | 'updates';
export type PushPreferences = Record<PushSwitch, boolean>;
export type AlertPreferences = Record<AlertChannel, PushPreferences>;

export const ALERT_CHANNELS: ReadonlyArray<{ key: AlertChannel; label: string; detail: string }> = [
  { key: 'push', label: 'Phone', detail: 'Your iPhone, when the app is installed and signed in.' },
  { key: 'email', label: 'Email', detail: 'Your sign-in address.' },
  { key: 'slack', label: 'Slack', detail: 'Your own notification channel, set in My Connectors. Never a shared channel.' },
];

export const PUSH_SWITCHES: ReadonlyArray<{ key: PushSwitch; label: string; detail: string }> = [
  { key: 'asks', label: 'Needs you', detail: 'A ball, an invitation, a request, a task, a reminder: something waiting on you.' },
  { key: 'live', label: 'Live now', detail: 'A session is opening and the room is waiting.' },
  { key: 'updates', label: 'Updates', detail: 'Results, plan versions and other news. Nothing is needed of you.' },
];

export const DEFAULT_ALERT_PREFERENCES: AlertPreferences = {
  push: { asks: true, live: true, updates: false },
  email: { asks: true, live: false, updates: false },
  slack: { asks: true, live: false, updates: false },
};

export const DEFAULT_PUSH_PREFERENCES: PushPreferences = DEFAULT_ALERT_PREFERENCES.push;

export function parsePreferences(raw: unknown, channel: AlertChannel = 'push'): PushPreferences {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const defaults = DEFAULT_ALERT_PREFERENCES[channel];
  const on = (key: PushSwitch) => (typeof row[key] === 'boolean' ? (row[key] as boolean) : defaults[key]);
  return { asks: on('asks'), live: on('live'), updates: on('updates') };
}

/** The phone's switches (1009's reader, kept for its callers). */
export const parsePushPreferences = (raw: unknown): PushPreferences => parsePreferences(raw, 'push');

type Rpc = (fn: string, args?: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

function rpc(): Rpc {
  const client = getTypedSupabaseClient() as unknown as { rpc: Rpc };
  return client.rpc.bind(client);
}

// reuse-ack: mirrors supabase/functions/_shared/pushNotifyCore.ts readChannelPreferences — the browser cannot import an edge function, and this one asks the server as the signed-in person
export async function fetchAlertPreferences(
  channel: AlertChannel,
): Promise<{ preferences: PushPreferences | null; error?: string }> {
  const { data, error } = await rpc()('get_notification_preferences', channel === 'push' ? {} : { p_channel: channel });
  return error ? { preferences: null, error: error.message } : { preferences: parsePreferences(data, channel) };
}

export async function setAlertPreference(
  channel: AlertChannel,
  key: PushSwitch,
  on: boolean,
): Promise<{ preferences: PushPreferences | null; error?: string }> {
  const { data, error } = await rpc()('set_notification_preferences', { [`p_${channel}`]: { [key]: on } });
  return error ? { preferences: null, error: error.message } : { preferences: parsePreferences(data, channel) };
}
